/**
 * Admin Role-Based Access Control helpers.
 *
 * Mirrors the backend ROLE_PERMISSIONS mapping so the frontend can
 * conditionally show/hide navigation items and display a 403 message
 * when a user navigates to a section they don't have access to.
 */

export const ROLE_PERMISSIONS = {
  super_admin: ["*"], // all access
  c_level: ["dashboard", "reports", "users", "settings", "campaigns", "finance"],
  admin: [
    "dashboard",
    "reports",
    "users",
    "tariffs",
    "campaigns",
    "moderation",
    "disputes",
    "finance",
    "support",
  ],
  finance: ["dashboard", "reports", "tariffs", "settings", "finance"],
  fraud: ["disputes", "moderation", "users"],
  government: ["dashboard", "reports", "users", "tariffs"],
  moderator: ["moderation", "disputes"],
};

export const READ_ONLY_ROLES = ["government"];

export const ADMIN_ROLE_LABELS = {
  super_admin: "Super Admin",
  c_level: "C-Level Executive",
  admin: "Admin",
  finance: "Finance",
  fraud: "Fraud",
  government: "Government",
  moderator: "Moderator",
};

/**
 * Check whether the given admin role has access to a specific section.
 * @param {string} role - The user's admin_role value (e.g. 'finance').
 * @param {string} section - The section key (e.g. 'dashboard', 'reports').
 * @returns {boolean}
 */
export function hasAdminAccess(role, section) {
  if (!role) return false;
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  if (permissions.includes("*")) return true;
  return permissions.includes(section);
}

/**
 * Check whether the user's role is read-only (no write operations).
 * @param {string} role
 * @returns {boolean}
 */
export function isReadOnlyRole(role) {
  return READ_ONLY_ROLES.includes(role);
}

/**
 * Returns the list of sections accessible by a given role.
 * @param {string} role
 * @returns {string[]}
 */
export function getAccessibleSections(role) {
  if (!role) return [];
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return [];
  if (permissions.includes("*")) {
    return [
      "dashboard",
      "reports",
      "users",
      "settings",
      "campaigns",
      "tariffs",
      "moderation",
      "disputes",
      "finance",
      "support",
    ];
  }
  return permissions;
}

/**
 * Extract admin_role from the JWT token stored in the authentication state.
 * Falls back to '' if the token doesn't contain admin_role.
 * @param {object} authUser - The authentication.user object from Redux state.
 * @returns {string} The admin_role value.
 */
export function getAdminRoleFromAuth(authUser) {
  if (!authUser) return "";

  // First, check if admin_role is directly on the auth object (set at login)
  if (authUser.admin_role) return authUser.admin_role;

  // Fallback: decode from JWT token
  if (authUser.jwt_token) {
    try {
      const payload = JSON.parse(atob(authUser.jwt_token.split(".")[1]));
      return payload.admin_role || "";
    } catch (e) {
      return "";
    }
  }

  return "";
}

/**
 * Default route each admin role should be redirected to when they try to
 * access a section they are not permitted to view.
 */
const ROLE_DEFAULT_ROUTES = {
  super_admin: "/admin-panel/dashboard",
  c_level: "/admin-panel/dashboard",
  admin: "/admin-panel/dashboard",
  finance: "/admin-panel/reports",
  fraud: "/admin-panel/disputes",
  government: "/admin-panel/dashboard",
  moderator: "/admin-panel/moderation",
};

/**
 * Return the default admin route for a given role.
 * Falls back to the main admin dashboard if the role is unknown.
 * @param {string} role
 * @returns {string}
 */
export function getDefaultAdminRoute(role) {
  return ROLE_DEFAULT_ROUTES[role] || "/admin-panel/dashboard";
}

/**
 * Map a route path to the section key used by ROLE_PERMISSIONS.
 * This allows AdminProtectedPage to determine which section a route belongs to.
 */
export const ROUTE_TO_SECTION = {
  "/admin-panel/dashboard": "dashboard",
  "/admin-panel/campaigns": "campaigns",
  "/admin-panel/settings": "settings",
  "/admin-panel/tariffs": "tariffs",
  "/admin-panel/users": "users",
  "/admin-panel/disputes": "disputes",
  "/admin-panel/reports": "reports",
  "/admin-panel/moderation": "moderation",
  "/admin-panel/eco-approvals": "moderation",
  "/admin-panel/announcements": "settings",
  "/admin-panel/support": "support",
  "/admin-panel/shipping-rates": "settings",
  "/admin-panel/tax-accounting": "finance",
  "/admin-panel/ads": "campaigns",
  "/admin-panel/platform-health": "dashboard",
  "/admin-panel/certifications": "moderation",
};

/**
 * Check whether the user is an admin (user_type === 'Admin').
 * Works with both the auth object and JWT token data.
 * @param {object} authUser - The authentication.user object from Redux state.
 * @returns {boolean}
 */
export function isAdminUser(authUser) {
  if (!authUser) return false;

  // Check direct property
  if (authUser.user_type === "Admin") return true;

  // Check JWT payload
  if (authUser.jwt_token) {
    try {
      const payload = JSON.parse(atob(authUser.jwt_token.split(".")[1]));
      // admin_role being set implies they are an admin
      return !!payload.admin_role;
    } catch (e) {
      return false;
    }
  }

  return false;
}
